import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Server,
  Plus,
  Trash2,
  CheckCircle2,
  Loader2,
  Pencil,
  Power,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  SlidersHorizontal,
  Variable,
  Copy,
  Shield,
  Globe,
  Database,
  RotateCcw,
  AlertTriangle,
  Clock,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import TeamMembers from "./TeamMembers";
import DataAuditTab from "./DataAuditTab";

const ENV_COLORS = {
  green: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", dot: "bg-emerald-500" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", dot: "bg-blue-500" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", dot: "bg-amber-500" },
  red: { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", dot: "bg-red-500" },
  purple: { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", dot: "bg-purple-500" },
  slate: { bg: "bg-slate-500/10", text: "text-slate-600 dark:text-slate-400", dot: "bg-slate-500" },
};

const FLAG_LABELS = {
  cost_intelligence: "Cost Intelligence",
  orphan_cleanup: "Orphan Cleanup Wizard",
  policy_compliance: "Policy Compliance",
  change_tracking: "Change Tracking",
  ai_briefings: "AI Briefings",
  drift_detection: "Drift Detection",
  export_reporting: "Export & Reporting",
};

const SETTING_LABELS = {
  log_level: { label: "Log Level", type: "select", options: ["debug", "info", "warning", "error"] },
  scan_interval_minutes: { label: "Scan Interval (min)", type: "select", options: ["5", "10", "15", "30", "60"] },
  auto_scan_enabled: { label: "Auto Scan", type: "toggle" },
  orphan_cooloff_days: { label: "Orphan Cool-off (days)", type: "number" },
  orphan_cost_threshold: { label: "Orphan Cost Threshold ($)", type: "number" },
  api_rate_limit: { label: "API Rate Limit (req/min)", type: "number" },
  data_retention_days: { label: "Data Retention (days)", type: "number" },
};

export default function EnvironmentsTab({ api, isAdmin }) {
  const [environments, setEnvironments] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [defaults, setDefaults] = useState({ flags: {}, settings: {} });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addCredEnvId, setAddCredEnvId] = useState(null);
  const [editCredId, setEditCredId] = useState(null);  // For editing existing credentials
  const [credForm, setCredForm] = useState({ name: "", subscription_id: "", tenant_id: "", client_id: "", client_secret: "" });
  const [savingCred, setSavingCred] = useState(false);
  
  // Soft Delete UI State
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [restoring, setRestoring] = useState(null);

  const fetchEnvironments = useCallback(async () => {
    try {
      const [envRes, credRes, depRes] = await Promise.all([
        api().get(`/settings/environments${showDeleted ? '?include_deleted=true' : ''}`),
        api().get("/credentials"),
        api().get("/deployments"),
      ]);
      setEnvironments(envRes.data.environments || []);
      setActiveId(envRes.data.active_id);
      setDefaults({ flags: envRes.data.default_feature_flags || {}, settings: envRes.data.default_settings || {} });
      setCredentials(credRes.data || []);
      setDeployments(depRes.data.deployments || []);
    } catch {
      toast.error("Failed to load environments");
    } finally {
      setLoading(false);
    }
  }, [api, showDeleted]);

  useEffect(() => { fetchEnvironments(); }, [fetchEnvironments]);

  const handleActivate = async (envId) => {
    try {
      await api().put(`/settings/environments/${envId}/activate`);
      setActiveId(envId);
      setEnvironments(prev => prev.map(e => ({ ...e, is_active: e.id === envId })));
      toast.success("Environment activated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
    }
  };

  const handleDelete = async (envId) => {
    try {
      const res = await api().delete(`/settings/environments/${envId}`);
      // Update environment status to soft_deleted instead of removing
      setEnvironments(prev => prev.map(e => 
        e.id === envId 
          ? { ...e, status: "soft_deleted", purge_after: res.data.purge_after }
          : e
      ));
      setDeleteConfirm(null);
      toast.success("Environment moved to trash. You can restore it within 30 days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleRestore = async (envId) => {
    setRestoring(envId);
    try {
      const res = await api().post(`/settings/environments/${envId}/restore`);
      setEnvironments(prev => prev.map(e => 
        e.id === envId ? res.data : e
      ));
      toast.success("Environment restored successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  // Calculate days remaining until purge
  const getDaysUntilPurge = (purgeAfter) => {
    if (!purgeAfter) return null;
    const purgeDate = new Date(purgeAfter);
    const now = new Date();
    const diff = Math.ceil((purgeDate - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const handleUpdate = async (envId, data) => {
    setSaving(true);
    try {
      const res = await api().put(`/settings/environments/${envId}`, data);
      setEnvironments(prev => prev.map(e => e.id === envId ? res.data : e));
      toast.success("Environment updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="environments-tab-content">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Deployment Environments</h3>
          <p className="text-[10px] text-[var(--n-text-muted)] mt-0.5">
            Manage feature flags, settings, and variables per environment. Only one environment can be active at a time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Show Deleted Toggle */}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleted(!showDeleted)}
              className={`text-[10px] h-8 px-2 ${showDeleted ? 'text-amber-600 bg-amber-500/10' : 'text-[var(--n-text-muted)]'}`}
              data-testid="toggle-show-deleted"
            >
              {showDeleted ? <Eye className="w-3.5 h-3.5 mr-1.5" /> : <EyeOff className="w-3.5 h-3.5 mr-1.5" />}
              {showDeleted ? 'Showing Deleted' : 'Show Deleted'}
            </Button>
          )}
          {/* New Environment button:
              - Only visible for system admins (isAdmin)
              - Enterprise users can also create (handled by API)
              - Non-enterprise users get auto-created env on signup */}
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => setShowCreate(true)}
              className="bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90 text-xs h-8"
              data-testid="create-env-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Environment
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {environments.length === 0 ? (
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] border-dashed">
            <CardContent className="py-12 text-center">
              <Server className="w-12 h-12 mx-auto text-[var(--n-text-muted)] mb-4 opacity-50" />
              <h3 className="text-sm font-medium mb-2">No Environments Yet</h3>
              <p className="text-[11px] text-[var(--n-text-muted)] max-w-md mx-auto mb-4">
                Environments are created when you provision a deployment. Each deployment provisions the infrastructure needed for your environment.
              </p>
              <Button
                size="sm"
                onClick={() => window.location.href = '/deployments/new'}
                className="bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90 text-xs h-8"
                data-testid="go-to-deployments-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Deployment
              </Button>
            </CardContent>
          </Card>
        ) : environments.map((env) => {
          const c = ENV_COLORS[env.color] || ENV_COLORS.blue;
          const isExpanded = expandedId === env.id;
          const isActive = env.id === activeId;
          const isSoftDeleted = env.status === "soft_deleted";
          const daysRemaining = getDaysUntilPurge(env.purge_after);

          // For soft-deleted environments, show a different card style
          if (isSoftDeleted) {
            return (
              <Card 
                key={env.id} 
                className="bg-red-500/5 border-red-500/20 opacity-75"
                data-testid={`env-card-deleted-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <CardHeader className="pb-3 pt-4 px-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-4 h-4 text-red-500" />
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-sm font-medium text-red-600 dark:text-red-400 line-through opacity-80">{env.name}</CardTitle>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-red-500/30 text-red-500">
                            Deleted
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-amber-500" />
                          <span className="text-[10px] text-amber-600 dark:text-amber-400">
                            {daysRemaining !== null 
                              ? `Permanently deletes in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                              : 'Scheduled for permanent deletion'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleRestore(env.id)}
                      disabled={restoring === env.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] h-8"
                      data-testid={`restore-env-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {restoring === env.id ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Restore
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            );
          }

          return (
            <Card key={env.id} className={`bg-[var(--n-card)] border-[var(--n-border-2)] transition-all ${isActive ? "ring-1 ring-[var(--hig-accent)]/30" : ""}`} data-testid={`env-card-${env.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardHeader className="pb-2 pt-5 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${c.dot} ${isActive ? "animate-pulse" : "opacity-50"}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-sm font-medium">{env.name}</CardTitle>
                        {isActive && <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-emerald-500/30 text-emerald-600 dark:text-emerald-400" data-testid="active-badge">Active</Badge>}
                      </div>
                      {env.description && <p className="text-[10px] text-[var(--n-text-muted)] mt-0.5">{env.description}</p>}
                      {env.deployment_id && (
                        <p className="text-[10px] text-[var(--hig-accent)] mt-0.5 flex items-center gap-1">
                          <Server className="w-3 h-3" /> Linked to deployment
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isAdmin && !isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleActivate(env.id)}
                        className="text-[10px] h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                        data-testid={`activate-env-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Power className="w-3 h-3 mr-1" /> Activate
                      </Button>
                    )}
                    {isAdmin && !isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm({ id: env.id, name: env.name })}
                        className="text-[10px] h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        data-testid={`delete-env-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedId(isExpanded ? null : env.id)}
                      className="h-7 w-7 p-0"
                      data-testid={`expand-env-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                {/* Summary row */}
                {!isExpanded && (
                  <div className="flex gap-4 mt-2 ml-5.5 pl-0.5">
                    <span className="text-[10px] text-[var(--n-text-muted)]">
                      {Object.values(env.feature_flags || {}).filter(Boolean).length}/{Object.keys(env.feature_flags || {}).length} features
                    </span>
                    <span className="text-[10px] text-[var(--n-text-muted)]">
                      {env.settings_overrides?.log_level || "info"} logging
                    </span>
                    <span className="text-[10px] text-[var(--n-text-muted)]">
                      {credentials.filter(c => c.environment_id === env.id).length} customer connections
                    </span>
                    {env.deployment_id && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                        Deployed
                      </span>
                    )}
                  </div>
                )}
              </CardHeader>

              {isExpanded && (
                <CardContent className="px-5 pb-5 pt-1 space-y-5">
                  {/* Linked Deployment */}
                  {(() => {
                    const dep = deployments.find(d => d.id === env.deployment_id);
                    return dep ? (
                      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15" data-testid={`env-deployment-${env.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Server className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Deployment: {dep.name}</span>
                          <Badge className="text-[8px] px-1 py-0 bg-emerald-500/10 text-emerald-500">{dep.status}</Badge>
                        </div>
                        <div className="flex gap-4 text-[10px] text-[var(--n-text-muted)]">
                          <span>{dep.compute_tier === "aks" ? "AKS" : "App Service"}</span>
                          <span>{dep.azure_config?.region}</span>
                          {dep.endpoint_url && <a href={dep.endpoint_url} target="_blank" rel="noopener noreferrer" className="text-[var(--hig-accent)] hover:underline truncate max-w-[200px]">{dep.endpoint_url}</a>}
                        </div>
                      </div>
                    ) : env.deployment_id ? (
                      <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs text-amber-600">
                        Deployment linked but not found. It may have been deleted.
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg border border-dashed border-[var(--n-border-2)] text-xs text-[var(--n-text-muted)] text-center">
                        No deployment linked. Create one from <a href="/deployments/new" className="text-[var(--hig-accent)] hover:underline">Cloud Infrastructure</a>.
                      </div>
                    );
                  })()}

                  {/* Customer Connections */}
                  {(() => {
                    const envCreds = credentials.filter(c => c.environment_id === env.id);
                    const unlinkedCreds = credentials.filter(c => !c.environment_id);
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Shield className="w-3.5 h-3.5 text-[var(--hig-accent)]" />
                            <span className="text-xs font-medium">Customer Connections</span>
                            <Badge variant="outline" className="text-[8px] px-1 py-0">{envCreds.length}</Badge>
                          </div>
                          {isAdmin && (
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" className="text-[10px] h-7 px-2"
                                onClick={async () => {
                                  try {
                                    const res = await api().get(`/azure-oauth/connect?environment_id=${env.id}`);
                                    if (res.data.auth_url) {
                                      window.location.href = res.data.auth_url;
                                    } else {
                                      toast.info(res.data.detail || res.data.message || "Scanner App not configured. Go to Deployments → Modify → Create Customer Auth first.");
                                    }
                                  } catch (err) {
                                    toast.info(err.response?.data?.detail || "Scanner App not configured. Go to Deployments → Modify → Create Customer Auth first.");
                                  }
                                }}
                                data-testid={`connect-azure-${env.id}`}
                              >
                                <Globe className="w-3 h-3 mr-1" /> Connect Azure
                              </Button>
                              <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2"
                                onClick={() => { setAddCredEnvId(env.id); setCredForm({ name: "", subscription_id: "", tenant_id: "", client_id: "", client_secret: "" }); }}
                                data-testid={`add-manual-cred-${env.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add Manual
                              </Button>
                            </div>
                          )}
                        </div>
                        {envCreds.length === 0 ? (
                          <p className="text-[10px] text-[var(--n-text-muted)] italic">No customers connected to this environment yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {envCreds.map(cred => (
                              <div key={cred.id} className="flex items-center justify-between p-2 rounded-lg bg-[var(--n-bg)] text-[11px]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Shield className="w-3 h-3 text-[var(--hig-accent)] shrink-0" />
                                  <span className="font-medium truncate">{cred.name}</span>
                                  <span className="text-[var(--n-text-muted)] font-mono text-[9px]">{cred.subscription_id?.slice(0, 12)}...</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-[8px] px-1 py-0 ${cred.auth_method === "oauth_consent" ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}`}>
                                    {cred.auth_method === "oauth_consent" ? "OAuth" : "SP"}
                                  </Badge>
                                  {isAdmin && (
                                    <button onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const res = await api().post(`/credentials/${cred.id}/test`);
                                        if (res.data.status === "success") toast.success(`${cred.name}: Connected (${res.data.resource_groups} RGs)`);
                                        else toast.error(`${cred.name}: ${res.data.message}`);
                                      } catch (err) { toast.error(err.response?.data?.detail || "Test failed"); }
                                    }} className="text-[var(--hig-accent)] hover:text-[var(--hig-accent-hover)] hover:bg-[var(--hig-accent-subtle)] p-2 rounded-md transition-colors" title="Test connection" data-testid={`test-cred-${cred.id}`}>
                                      <CheckCircle2 className="w-4 h-4" />
                                    </button>
                                  )}
                                  {isAdmin && cred.auth_method !== "oauth_consent" && (
                                    <button onClick={(e) => {
                                      e.stopPropagation();
                                      setEditCredId(cred.id);
                                      setCredForm({
                                        name: cred.name || "",
                                        subscription_id: cred.subscription_id || "",
                                        tenant_id: cred.tenant_id || "",
                                        client_id: cred.client_id || "",
                                        client_secret: ""  // Don't pre-fill secret
                                      });
                                    }} className="text-blue-400 hover:text-blue-500 hover:bg-blue-500/10 p-2 rounded-md transition-colors" title="Edit credential" data-testid={`edit-cred-${cred.id}`}>
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                  )}
                                  {isAdmin && (
                                    <button onClick={async () => {
                                      if (!window.confirm(`Remove ${cred.name}?`)) return;
                                      try { await api().delete(`/credentials/${cred.id}`); toast.success("Removed"); fetchEnvironments(); }
                                      catch { toast.error("Failed to remove"); }
                                    }} className="text-red-400 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-md transition-colors" title="Remove" data-testid={`delete-cred-${cred.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {unlinkedCreds.length > 0 && (
                          <div className="mt-2 p-2 rounded-lg border border-amber-500/20 bg-amber-500/5">
                            <p className="text-[10px] text-amber-600 font-medium mb-1.5">{unlinkedCreds.length} unlinked credential(s)</p>
                            {unlinkedCreds.map(cred => (
                              <div key={cred.id} className="flex items-center justify-between py-1 text-[10px]">
                                <span className="text-[var(--n-text-secondary)]">{cred.name} <span className="text-[var(--n-text-muted)] font-mono">({cred.subscription_id?.slice(0, 8)}...)</span></span>
                                {isAdmin && (
                                  <Button size="sm" variant="ghost" className="text-[9px] h-5 px-1.5 text-[var(--hig-accent)]"
                                    onClick={async () => {
                                      try {
                                        await api().post("/credentials", { ...cred, environment_id: env.id });
                                        toast.success(`Linked ${cred.name} to ${env.name}`);
                                        fetchEnvironments();
                                      } catch {
                                        // Try updating instead
                                        try {
                                          await api().put(`/credentials/${cred.id}`, { environment_id: env.id });
                                          toast.success(`Linked ${cred.name} to ${env.name}`);
                                          fetchEnvironments();
                                        } catch { toast.error("Failed to link"); }
                                      }
                                    }}
                                  >Link here</Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Feature Flags */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <ToggleLeft className="w-3.5 h-3.5 text-[var(--hig-accent)]" />
                      <span className="text-xs font-medium">Feature Flags</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6">
                      {Object.entries(FLAG_LABELS).map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-xs text-[var(--n-text-secondary)]">{label}</span>
                          <Switch
                            checked={env.feature_flags?.[key] ?? true}
                            onCheckedChange={(v) => handleUpdate(env.id, { feature_flags: { [key]: v } })}
                            disabled={!isAdmin}
                            data-testid={`flag-${key}-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Settings Overrides */}
                  <div className="pt-3 border-t border-[var(--n-border-2)]">
                    <div className="flex items-center gap-2 mb-3">
                      <SlidersHorizontal className="w-3.5 h-3.5 text-[var(--hig-accent)]" />
                      <span className="text-xs font-medium">Settings Overrides</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {Object.entries(SETTING_LABELS).map(([key, meta]) => {
                        const val = env.settings_overrides?.[key];
                        if (meta.type === "toggle") {
                          return (
                            <div key={key} className="flex items-center justify-between">
                              <span className="text-xs text-[var(--n-text-secondary)]">{meta.label}</span>
                              <Switch
                                checked={val ?? true}
                                onCheckedChange={(v) => handleUpdate(env.id, { settings_overrides: { [key]: v } })}
                                disabled={!isAdmin}
                                data-testid={`setting-${key}-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                              />
                            </div>
                          );
                        }
                        if (meta.type === "select") {
                          return (
                            <div key={key} className="space-y-1">
                              <Label className="text-xs text-[var(--n-text-secondary)]">{meta.label}</Label>
                              <Select
                                value={String(val ?? meta.options[Math.floor(meta.options.length / 2)])}
                                onValueChange={(v) => handleUpdate(env.id, { settings_overrides: { [key]: isNaN(v) ? v : parseInt(v) } })}
                                disabled={!isAdmin}
                              >
                                <SelectTrigger className="h-8 text-xs bg-[var(--n-bg)] border-[var(--n-border-2)]" data-testid={`setting-${key}-${env.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {meta.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }
                        return (
                          <div key={key} className="space-y-1">
                            <Label className="text-xs text-[var(--n-text-secondary)]">{meta.label}</Label>
                            <Input
                              type="number"
                              min="0"
                              value={val ?? 0}
                              onChange={(e) => handleUpdate(env.id, { settings_overrides: { [key]: parseFloat(e.target.value) || 0 } })}
                              className="h-8 text-xs bg-[var(--n-bg)] border-[var(--n-border-2)]"
                              disabled={!isAdmin}
                              data-testid={`setting-${key}-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Custom Variables */}
                  <div className="pt-3 border-t border-[var(--n-border-2)]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Variable className="w-3.5 h-3.5 text-[var(--hig-accent)]" />
                        <span className="text-xs font-medium">Custom Variables</span>
                      </div>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const key = prompt("Variable name (e.g. WEBHOOK_URL):");
                            if (!key) return;
                            const value = prompt(`Value for ${key}:`);
                            if (value === null) return;
                            handleUpdate(env.id, { variables: { ...env.variables, [key]: value } });
                          }}
                          className="text-[10px] h-6 px-2"
                          data-testid={`add-variable-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                    {Object.keys(env.variables || {}).length === 0 ? (
                      <p className="text-[10px] text-[var(--n-text-muted)] italic">No custom variables defined.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {Object.entries(env.variables || {}).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-2 py-1 px-2 rounded bg-[var(--n-bg)] text-xs">
                            <code className="font-mono text-[10px] text-[var(--hig-accent)] min-w-[120px]">{k}</code>
                            <span className="text-[var(--n-text-secondary)] flex-1 truncate">{v}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 opacity-50 hover:opacity-100"
                              onClick={() => { navigator.clipboard.writeText(v); toast.success("Copied"); }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 text-red-500 opacity-50 hover:opacity-100"
                                onClick={() => {
                                  const updated = { ...env.variables };
                                  delete updated[k];
                                  handleUpdate(env.id, { variables: updated });
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Team Members (Phase 7) */}
                  <div className="pt-3 border-t border-[var(--n-border-2)]">
                    <TeamMembers
                      api={api}
                      environmentId={env.id}
                      environmentName={env.name}
                      isOwner={env.user_id === activeId || isAdmin}
                      isOperator={env._access === "operator"}
                    />
                  </div>

                  {/* Data Audit (Tier 3) */}
                  <div className="pt-3 border-t border-[var(--n-border-2)]">
                    <DataAuditTab
                      api={api}
                      environmentId={env.id}
                      environmentName={env.name}
                    />
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Create Environment Dialog */}
      <CreateEnvironmentDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        api={api}
        deployments={deployments}
        isAdmin={isAdmin}
        onCreated={(env) => { setEnvironments(prev => [...prev, env]); setShowCreate(false); }}
      />

      {/* Add Credential Dialog */}
      <Dialog open={!!addCredEnvId} onOpenChange={(open) => !open && setAddCredEnvId(null)}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-[var(--hig-accent)]" /> Add Customer Connection
            </DialogTitle>
            <DialogDescription className="text-[10px]">
              Add an Azure subscription to scan. Provide the customer's Service Principal credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Connection Name *</Label>
              <Input placeholder="e.g. Acme Corp" value={credForm.name} onChange={e => setCredForm(p => ({ ...p, name: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm" data-testid="cred-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Subscription ID *</Label>
                <Input placeholder="xxxxxxxx-xxxx-xxxx-..." value={credForm.subscription_id} onChange={e => setCredForm(p => ({ ...p, subscription_id: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="cred-sub-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tenant ID</Label>
                <Input placeholder="xxxxxxxx-xxxx-xxxx-..." value={credForm.tenant_id} onChange={e => setCredForm(p => ({ ...p, tenant_id: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="cred-tenant-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Client ID</Label>
                <Input placeholder="Service Principal App ID" value={credForm.client_id} onChange={e => setCredForm(p => ({ ...p, client_id: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="cred-clientid-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Client Secret</Label>
                <Input type="password" placeholder="Service Principal secret" value={credForm.client_secret} onChange={e => setCredForm(p => ({ ...p, client_secret: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="cred-secret-input" />
              </div>
            </div>
            <Button
              onClick={async () => {
                if (!credForm.name.trim() || !credForm.subscription_id.trim()) { toast.error("Name and Subscription ID are required"); return; }
                setSavingCred(true);
                try {
                  await api().post("/credentials", { ...credForm, auth_method: "service_principal", environment_id: addCredEnvId });
                  toast.success(`Connection "${credForm.name}" added`);
                  setAddCredEnvId(null);
                  fetchEnvironments();
                } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
                finally { setSavingCred(false); }
              }}
              disabled={savingCred || !credForm.name.trim() || !credForm.subscription_id.trim()}
              className="w-full bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90"
              data-testid="cred-submit-btn"
            >
              {savingCred ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Connection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Credential Dialog */}
      <Dialog open={!!editCredId} onOpenChange={(open) => !open && setEditCredId(null)}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm">
              <Pencil className="w-4 h-4 text-blue-400" /> Edit Customer Connection
            </DialogTitle>
            <DialogDescription className="text-[10px]">
              Update the connection details. Leave Client Secret empty to keep the existing secret.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Connection Name *</Label>
              <Input placeholder="e.g. Acme Corp" value={credForm.name} onChange={e => setCredForm(p => ({ ...p, name: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm" data-testid="edit-cred-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Subscription ID *</Label>
                <Input placeholder="xxxxxxxx-xxxx-xxxx-..." value={credForm.subscription_id} onChange={e => setCredForm(p => ({ ...p, subscription_id: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="edit-cred-sub-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tenant ID</Label>
                <Input placeholder="xxxxxxxx-xxxx-xxxx-..." value={credForm.tenant_id} onChange={e => setCredForm(p => ({ ...p, tenant_id: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="edit-cred-tenant-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Client ID</Label>
                <Input placeholder="Service Principal App ID" value={credForm.client_id} onChange={e => setCredForm(p => ({ ...p, client_id: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="edit-cred-clientid-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Client Secret</Label>
                <Input type="password" placeholder="Leave empty to keep existing" value={credForm.client_secret} onChange={e => setCredForm(p => ({ ...p, client_secret: e.target.value }))} className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm font-mono" data-testid="edit-cred-secret-input" />
              </div>
            </div>
            <Button
              onClick={async () => {
                if (!credForm.name.trim() || !credForm.subscription_id.trim()) { toast.error("Name and Subscription ID are required"); return; }
                setSavingCred(true);
                try {
                  const payload = { 
                    name: credForm.name.trim(),
                    subscription_id: credForm.subscription_id.trim(),
                    tenant_id: credForm.tenant_id.trim(),
                    client_id: credForm.client_id.trim(),
                  };
                  // Only include secret if provided (allows keeping existing secret)
                  if (credForm.client_secret.trim()) {
                    payload.client_secret = credForm.client_secret.trim();
                  }
                  await api().put(`/credentials/${editCredId}`, payload);
                  toast.success(`Connection "${credForm.name}" updated`);
                  setEditCredId(null);
                  setCredForm({ name: "", subscription_id: "", tenant_id: "", client_id: "", client_secret: "" });
                  fetchEnvironments();
                } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
                finally { setSavingCred(false); }
              }}
              disabled={savingCred || !credForm.name.trim() || !credForm.subscription_id.trim()}
              className="w-full bg-blue-500 text-white hover:bg-blue-600"
              data-testid="edit-cred-submit-btn"
            >
              {savingCred ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Update Connection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm text-red-500">
              <AlertTriangle className="w-4 h-4" /> Delete Environment
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              This will soft-delete the environment. You'll have 30 days to restore it before permanent deletion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">30-Day Grace Period</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You can restore <strong>"{deleteConfirm?.name}"</strong> within 30 days. After that, all data will be permanently deleted and cannot be recovered.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border-[var(--n-border-2)]"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => deleteConfirm && handleDelete(deleteConfirm.id)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateEnvironmentDialog({ open, onOpenChange, api, onCreated, deployments = [], isAdmin = false }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState("blue");
  const [deploymentId, setDeploymentId] = useState("");
  const [creating, setCreating] = useState(false);

  // Filter deployments that are ready for environment creation
  // For default deployment: always show (can have multiple environments)
  // For non-default: only show if no environment linked yet
  const availableDeployments = deployments.filter(d => {
    const isReady = ["provisioned", "active", "deployed"].includes(d.status);
    const isDefault = d.is_default === true;
    const hasNoEnvironment = !d.environment_id;
    return isReady && (isDefault || hasNoEnvironment);
  });

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!deploymentId) { toast.error("Please select a deployment"); return; }
    setCreating(true);
    try {
      const res = await api().post("/settings/environments", { 
        name: name.trim(), 
        description: desc.trim(), 
        color,
        deployment_id: deploymentId  // Required
      });
      toast.success(`Environment "${name}" created`);
      onCreated(res.data);
      setName(""); setDesc(""); setColor("blue"); setDeploymentId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Creation failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-sm">
            <Server className="w-4 h-4 text-[var(--hig-accent)]" /> New Environment
          </DialogTitle>
          <DialogDescription className="text-[10px]">
            Link an environment to a deployment. Select from available data planes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Deployment selection - required */}
          <div className="space-y-1.5">
            <Label className="text-xs">Deployment *</Label>
            <Select value={deploymentId} onValueChange={setDeploymentId}>
              <SelectTrigger className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm" data-testid="env-deployment-select">
                <SelectValue placeholder="Select a deployment..." />
              </SelectTrigger>
              <SelectContent>
                {availableDeployments.length === 0 ? (
                  <div className="p-2 text-xs text-[var(--n-text-muted)]">
                    No available deployments. Create one first or contact support.
                  </div>
                ) : (
                  availableDeployments.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      <span className="flex items-center gap-2">
                        {d.name}
                        {d.is_default && (
                          <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 font-medium">
                            SHARED
                          </span>
                        )}
                        <span className="text-[10px] text-[var(--n-text-muted)]">({d.status})</span>
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {availableDeployments.length > 0 && (
              <p className="text-[9px] text-[var(--n-text-muted)]">
                {availableDeployments.some(d => d.is_default) 
                  ? "SHARED = Niyant's managed data plane (included in subscription)"
                  : "Select your dedicated data plane deployment"}
              </p>
            )}
          </div>
          
          <div className="space-y-1.5">
            <Label className="text-xs">Name *</Label>
            <Input
              placeholder="e.g. Production, Staging"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm"
              data-testid="env-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Description</Label>
            <Input
              placeholder="Optional description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="bg-[var(--n-bg)] border-[var(--n-border-2)] text-sm"
              data-testid="env-desc-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Color</Label>
            <div className="flex gap-2">
              {Object.entries(ENV_COLORS).map(([key, c]) => (
                <button
                  key={key}
                  onClick={() => setColor(key)}
                  className={`w-7 h-7 rounded-full ${c.dot} transition-all ${color === key ? "ring-2 ring-offset-2 ring-offset-[var(--n-card)] ring-[var(--hig-accent)]" : "opacity-50 hover:opacity-75"}`}
                  data-testid={`env-color-${key}`}
                />
              ))}
            </div>
          </div>
          <Button
            onClick={handleCreate}
            disabled={creating || !name.trim() || !deploymentId}
            className="w-full bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90"
            data-testid="create-env-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Environment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
